import React, { useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiUtils } from '../utils/api';

// Image dropzone component with URL input
function ImageDropzone({ value, onChange, label = 'Image', previewHeight = 'h-48' }) {
  const onDrop = (acceptedFiles) => {
    const file = acceptedFiles?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => onChange(String(reader.result));
    reader.readAsDataURL(file);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    multiple: false,
    maxFiles: 1,
  });

  return (
    <div className="space-y-2">
      <div
        {...getRootProps()}
        className={
          `w-full border-2 border-dashed rounded-xl p-4 cursor-pointer transition-colors ` +
          (isDragActive ? 'border-brand-500 bg-brand-50' : 'border-gray-300 hover:bg-gray-50')
        }
      >
        <input {...getInputProps()} />
        <div className="text-sm text-gray-700">
          {isDragActive ? 'Drop the image here…' : `Drag & drop ${label.toLowerCase()} here, or click to select`}
        </div>
      </div>

      <input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Or paste image URL here"
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
      />

      {value ? (
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <img src={value} alt={`${label} preview`} className={`w-full ${previewHeight} object-cover`} />
        </div>
      ) : null}
    </div>
  );
}

export default function AboutPageEditor() {
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(null);

  const [heroOpen, setHeroOpen] = useState(false);
  const [storyOpen, setStoryOpen] = useState(false);
  const [mvvOpen, setMvvOpen] = useState(false);
  const [whatWeDoOpen, setWhatWeDoOpen] = useState(false);
  const [expertiseOpen, setExpertiseOpen] = useState(false);
  const [teamOpen, setTeamOpen] = useState(false);
  const [whyChooseUsOpen, setWhyChooseUsOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [infrastructureOpen, setInfrastructureOpen] = useState(false);
  const [ctaOpen, setCtaOpen] = useState(false);

  const [hero, setHero] = useState({
    badge: '✨ Who We Are',
    headingLine1: 'About',
    headingLine2: 'Our Company',
    description: 'We are committed to delivering high-quality products and solutions, driven by innovation, integrity, and long-term partnerships across global markets.',
    features: 'Quality Driven • Globally Trusted • Customer Focused',
    isActive: true,
  });

  const [story, setStory] = useState({
    image: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d',
    imageAlt: 'Office',
    title: 'Our Story',
    paragraph1: 'Founded in 2010, our company began with a simple vision: to revolutionize the industry through cutting-edge solutions and exceptional service. What started as a small team of passionate innovators has grown into a global leader.',
    paragraph2: 'Over the past decade, we\'ve served thousands of clients across multiple continents, delivering transformative results and building lasting partnerships. Our commitment to excellence and continuous innovation has made us a trusted name in the industry.',
    ctaText: 'Learn More',
    ctaLink: '#',
    isActive: true,
  });

  const [mvv, setMvv] = useState({
    sectionTitle: 'Our Mission, Vision & Values',
    missionTitle: 'Mission',
    missionText: 'To deliver innovative solutions that empower businesses.',
    visionTitle: 'Vision',
    visionText: 'To be the global leader recognized for innovation and excellence.',
    valuesTitle: 'Core Values',
    values: ['Innovation', 'Integrity', 'Excellence', 'Customer Focus'],
    isActive: true,
  });

  const [whatWeDo, setWhatWeDo] = useState({
    title: 'What We Do',
    services: [
      {
        title: 'Strategic Consulting',
        description: 'We help organizations develop strategies aligned with their goals.',
      },
      {
        title: 'Implementation & Support',
        description: 'End-to-end implementation with dedicated support.',
      },
      {
        title: 'Technology Solutions',
        description: 'Technology solutions that drive digital transformation.',
      },
      {
        title: 'Training & Development',
        description: 'Training programs to maximize value from solutions.',
      },
    ],
    isActive: true,
  });

  const [expertise, setExpertise] = useState({
    title: 'Our Expertise',
    items: [
      'Cloud Infrastructure',
      'Data Analytics',
      'AI & Machine Learning',
      'Cybersecurity',
      'Mobile Development',
      'Enterprise Solutions',
      'IoT Integration',
      'Blockchain',
    ],
    isActive: true,
  });

  const [team, setTeam] = useState({
    title: 'Our Leadership Team',
    members: [
      {
        name: 'Isabella Garcia',
        position: 'Logistics & Customs Manager',
        bio: 'Dedicated professional bringing expertise and leadership to drive organizational success and innovation.',
        image: 'https://static.wixstatic.com/media/63ad60_8f4282416320466ba21f74b209c819ad~mv2.png/v1/fill/w_330,h_240,al_c,q_85,usm_0.66_1.00_0.01,enc_auto/63ad60_8f4282416320466ba21f74b209c819ad~mv2.png',
      },
      {
        name: 'Liam O\'Connell',
        position: 'International Trade Specialist',
        bio: 'Dedicated professional bringing expertise and leadership to drive organizational success and innovation.',
        image: 'https://static.wixstatic.com/media/63ad60_5a0cfb57b41b4f22ad4e3b46a2f30746~mv2.png/v1/fill/w_330,h_240,al_c,q_85,usm_0.66_1.00_0.01,enc_auto/63ad60_5a0cfb57b41b4f22ad4e3b46a2f30746~mv2.png',
      },
      {
        name: 'Chloe Davis',
        position: 'Client Relations Manager',
        bio: 'Dedicated professional bringing expertise and leadership to drive organizational success and innovation.',
        image: 'https://static.wixstatic.com/media/63ad60_8f4282416320466ba21f74b209c819ad~mv2.png/v1/fill/w_330,h_240,al_c,q_85,usm_0.66_1.00_0.01,enc_auto/63ad60_8f4282416320466ba21f74b209c819ad~mv2.png',
      },
      {
        name: 'Noah Kim',
        position: 'Head of Technology & Innovation',
        bio: 'Dedicated professional bringing expertise and leadership to drive organizational success and innovation.',
        image: 'https://static.wixstatic.com/media/63ad60_5a0cfb57b41b4f22ad4e3b46a2f30746~mv2.png/v1/fill/w_330,h_240,al_c,q_85,usm_0.66_1.00_0.01,enc_auto/63ad60_5a0cfb57b41b4f22ad4e3b46a2f30746~mv2.png',
      },
      {
        name: 'Evelyn Reed',
        position: 'Chief Executive Officer',
        bio: 'Dedicated professional bringing expertise and leadership to drive organizational success and innovation.',
        image: 'https://static.wixstatic.com/media/63ad60_8f4282416320466ba21f74b209c819ad~mv2.png/v1/fill/w_330,h_240,al_c,q_85,usm_0.66_1.00_0.01,enc_auto/63ad60_8f4282416320466ba21f74b209c819ad~mv2.png',
      },
      {
        name: 'Marcus Thorne',
        position: 'Head of Global Operations',
        bio: 'Dedicated professional bringing expertise and leadership to drive organizational success and innovation.',
        image: 'https://static.wixstatic.com/media/63ad60_5a0cfb57b41b4f22ad4e3b46a2f30746~mv2.png/v1/fill/w_330,h_240,al_c,q_85,usm_0.66_1.00_0.01,enc_auto/63ad60_5a0cfb57b41b4f22ad4e3b46a2f30746~mv2.png',
      },
      {
        name: 'Sophia Chen',
        position: 'Director of Sales & Marketing',
        bio: 'Dedicated professional bringing expertise and leadership to drive organizational success and innovation.',
        image: 'https://static.wixstatic.com/media/63ad60_8f4282416320466ba21f74b209c819ad~mv2.png/v1/fill/w_330,h_240,al_c,q_85,usm_0.66_1.00_0.01,enc_auto/63ad60_8f4282416320466ba21f74b209c819ad~mv2.png',
      },
    ],
    isActive: true,
  });

  const [gallery, setGallery] = useState({
    title: 'Gallery',
    images: Array.from({ length: 12 }, (_, i) => ({
      title: `Gallery Title ${i + 1}`,
      description: 'Short description here',
      image: i % 6 === 0 ? 'https://static.wixstatic.com/media/63ad60_96db1c774fb04c3386f4e74deaeca5b9~mv2.png/v1/fill/w_330,h_240,al_c,q_85,usm_0.66_1.00_0.01,enc_auto/63ad60_96db1c774fb04c3386f4e74deaeca5b9~mv2.png'
        : i % 6 === 1 ? 'https://static.wixstatic.com/media/63ad60_cc5d56fd4ecf498c9126f6d1bec8d1cb~mv2.png/v1/fill/w_330,h_240,al_c,q_85,usm_0.66_1.00_0.01,enc_auto/63ad60_cc5d56fd4ecf498c9126f6d1bec8d1cb~mv2.png'
        : i % 6 === 2 ? 'https://static.wixstatic.com/media/63ad60_10c1d3597ae649929701cee318c1d8f2~mv2.png/v1/fill/w_330,h_240,al_c,q_85,usm_0.66_1.00_0.01,enc_auto/63ad60_10c1d3597ae649929701cee318c1d8f2~mv2.png'
        : i % 6 === 3 ? 'https://static.wixstatic.com/media/63ad60_88e5d7f2ba7c4b40b441e63c5e15b42c~mv2.png/v1/fill/w_330,h_240,al_c,q_85,usm_0.66_1.00_0.01,enc_auto/63ad60_88e5d7f2ba7c4b40b441e63c5e15b42c~mv2.png'
        : i % 6 === 4 ? 'https://static.wixstatic.com/media/63ad60_a5df87f4764b4572af50c271de5e847e~mv2.png/v1/fill/w_330,h_240,al_c,q_85,usm_0.66_1.00_0.01,enc_auto/63ad60_a5df87f4764b4572af50c271de5e847e~mv2.png'
        : 'https://static.wixstatic.com/media/63ad60_6f035dbaa6974d34a49a69ef376a16b7~mv2.png/v1/fill/w_330,h_240,al_c,q_85,usm_0.66_1.00_0.01,enc_auto/63ad60_6f035dbaa6974d34a49a69ef376a16b7~mv2.png',
      alt: `Gallery ${i + 1}`,
    })),
    isActive: true,
  });

  const [infrastructure, setInfrastructure] = useState({
    title: 'Our Infrastructure',
    features: [
      {
        title: 'Global Data Centers',
        description: 'Our infrastructure spans across 6 continents with redundant data centers ensuring 99.99% availability and minimal latency for our clients worldwide.',
      },
      {
        title: 'Advanced Security',
        description: 'Multi-layer security architecture with end-to-end encryption, advanced threat detection, and continuous monitoring to protect your data.',
      },
      {
        title: 'Scalable Architecture',
        description: 'Cloud-native infrastructure that automatically scales to handle peak loads while maintaining optimal performance and cost efficiency.',
      },
    ],
    highlights: [
      '6 Global Data Centers',
      '99.99% Uptime SLA',
      'Real-time Backup & Recovery',
      'DDoS Protection',
      'Load Balancing',
      'Auto-scaling Capabilities',
      'CDN Integration',
      'API Rate Limiting',
    ],
    highlightsTitle: 'Infrastructure Highlights',
    isActive: true,
  });

  const [cta, setCta] = useState({
    heading: 'Ready to Work With Us?',
    description: 'Let\'s discuss how we can help transform your business and achieve your\ngoals.',
    buttonText: 'Get In Touch',
    buttonLink: '/contact',
    isActive: true,
  });

  const [whyChooseUs, setWhyChooseUs] = useState({
    title: 'Why Choose Us',
    reasons: [
      {
        icon: 'award',
        title: 'Proven Track Record',
        description: 'Over 15 years of delivering exceptional results for Fortune 500 companies and innovative startups.',
      },
      {
        icon: 'target',
        title: 'Customer-Centric Approach',
        description: 'We prioritize understanding your unique needs and delivering tailored solutions that drive real impact.',
      },
      {
        icon: 'globe',
        title: 'Global Reach',
        description: 'With offices in 25+ countries, we provide local expertise with global resources.',
      },
      {
        icon: 'users',
        title: 'Expert Team',
        description: 'Our team comprises industry veterans with deep expertise across multiple domains and technologies.',
      },
      {
        icon: 'lightbulb',
        title: 'Innovation First',
        description: 'We stay ahead of industry trends and continuously invest in research and development.',
      },
      {
        icon: 'clock',
        title: '24/7 Support',
        description: 'Our dedicated support team is available round-the-clock to ensure your success.',
      },
    ],
    achievements: [
      {
        title: 'ISO 27001 Certified',
        description: 'Information Security Management',
      },
      {
        title: 'ISO 9001 Certified',
        description: 'Quality Management System',
      },
      {
        title: 'SOC 2 Type II',
        description: 'Security & Compliance',
      },
      {
        title: '10,000+ Clients Served',
        description: 'Global Customer Base',
      },
      {
        title: '99.9% Uptime',
        description: 'Service Reliability',
      },
      {
        title: '50+ Industry Awards',
        description: 'Recognition & Excellence',
      },
    ],
    isActive: true,
  });

  const heroSectionId = useMemo(() => {
    const existing = (page?.sections || []).find(s => !s.isGlobal && s.sectionType === 'hero');
    return existing?.sectionId || 'hero-1';
  }, [page]);

  const storySectionId = useMemo(() => {
    const existing = (page?.sections || []).find(s => !s.isGlobal && s.sectionType === 'story');
    return existing?.sectionId || 'story-1';
  }, [page]);

  const mvvSectionId = useMemo(() => {
    const existing = (page?.sections || []).find(s => !s.isGlobal && s.sectionType === 'mvv');
    return existing?.sectionId || 'mvv-1';
  }, [page]);

  const whatWeDoSectionId = useMemo(() => {
    const existing = (page?.sections || []).find(s => !s.isGlobal && s.sectionType === 'what-we-do');
    return existing?.sectionId || 'what-we-do-1';
  }, [page]);

  const expertiseSectionId = useMemo(() => {
    const existing = (page?.sections || []).find(s => !s.isGlobal && s.sectionType === 'expertise');
    return existing?.sectionId || 'expertise-1';
  }, [page]);

  const teamSectionId = useMemo(() => {
    const existing = (page?.sections || []).find(s => !s.isGlobal && s.sectionType === 'team');
    return existing?.sectionId || 'team-1';
  }, [page]);

  const whyChooseUsSectionId = useMemo(() => {
    const existing = (page?.sections || []).find(s => !s.isGlobal && s.sectionType === 'why-choose-us');
    return existing?.sectionId || 'why-choose-us-1';
  }, [page]);

  const gallerySectionId = useMemo(() => {
    const existing = (page?.sections || []).find(s => !s.isGlobal && s.sectionType === 'gallery');
    return existing?.sectionId || 'gallery-1';
  }, [page]);

  const infrastructureSectionId = useMemo(() => {
    const existing = (page?.sections || []).find(s => !s.isGlobal && s.sectionType === 'infrastructure');
    return existing?.sectionId || 'infrastructure-1';
  }, [page]);

  const ctaSectionId = useMemo(() => {
    const existing = (page?.sections || []).find(s => !s.isGlobal && s.sectionType === 'cta');
    return existing?.sectionId || 'cta-1';
  }, [page]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const res = await apiUtils.cms.getPage('about');

      setLoading(false);
      if (!res.success) {
        toast.error(res.error || 'Failed to load about page');
        // Still allow editing/saving by initializing an empty page model
        setPage({ pageName: 'about', metaTitle: '', metaDescription: '', sections: [] });
        return;
      }

      setPage(res.data);

      const heroSection = (res.data?.sections || []).find(s => s.sectionType === 'hero' && !s.isGlobal);
      const c = heroSection?.content || heroSection || {};
      setHero({
        badge: c.badge || '✨ Who We Are',
        headingLine1: c.headingLine1 || 'About',
        headingLine2: c.headingLine2 || 'Our Company',
        description: c.description || '',
        features: c.features || 'Quality Driven • Globally Trusted • Customer Focused',
        isActive: heroSection?.isActive ?? true,
      });

      const storySection = (res.data?.sections || []).find(s => s.sectionType === 'story' && !s.isGlobal);
      const sc = storySection?.content || storySection || {};
      setStory({
        image: sc.image || 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d',
        imageAlt: sc.imageAlt || 'Office',
        title: sc.title || 'Our Story',
        paragraph1: sc.paragraph1 || '',
        paragraph2: sc.paragraph2 || '',
        ctaText: sc.ctaText || 'Learn More',
        ctaLink: sc.ctaLink || '#',
        isActive: storySection?.isActive ?? true,
      });

      const mvvSection = (res.data?.sections || []).find(s => s.sectionType === 'mvv' && !s.isGlobal);
      const mc = mvvSection?.content || mvvSection || {};
      setMvv({
        sectionTitle: mc.sectionTitle || 'Our Mission, Vision & Values',
        missionTitle: mc.missionTitle || 'Mission',
        missionText: mc.missionText || '',
        visionTitle: mc.visionTitle || 'Vision',
        visionText: mc.visionText || '',
        valuesTitle: mc.valuesTitle || 'Core Values',
        values: Array.isArray(mc.values) ? mc.values : ['Innovation', 'Integrity', 'Excellence', 'Customer Focus'],
        isActive: mvvSection?.isActive ?? true,
      });

      const whatWeDoSection = (res.data?.sections || []).find(s => s.sectionType === 'what-we-do' && !s.isGlobal);
      const wc = whatWeDoSection?.content || whatWeDoSection || {};
      setWhatWeDo(prev => ({
        ...prev,
        title: wc.title || 'What We Do',
        services: Array.isArray(wc.services) ? wc.services : prev.services,
        isActive: whatWeDoSection?.isActive ?? true,
      }));

      const expertiseSection = (res.data?.sections || []).find(s => s.sectionType === 'expertise' && !s.isGlobal);
      const ec = expertiseSection?.content || expertiseSection || {};
      setExpertise(prev => ({
        ...prev,
        title: ec.title || 'Our Expertise',
        items: Array.isArray(ec.items) ? ec.items : prev.items,
        isActive: expertiseSection?.isActive ?? true,
      }));

      const teamSection = (res.data?.sections || []).find(s => s.sectionType === 'team' && !s.isGlobal);
      const tc = teamSection?.content || teamSection || {};
      setTeam(prev => ({
        ...prev,
        title: tc.title || 'Our Leadership Team',
        members: Array.isArray(tc.members) ? tc.members : prev.members,
        isActive: teamSection?.isActive ?? true,
      }));

      const whyChooseUsSection = (res.data?.sections || []).find(s => s.sectionType === 'why-choose-us' && !s.isGlobal);
      const wcc = whyChooseUsSection?.content || whyChooseUsSection || {};
      setWhyChooseUs(prev => ({
        ...prev,
        title: wcc.title || 'Why Choose Us',
        reasons: Array.isArray(wcc.reasons) ? wcc.reasons : prev.reasons,
        achievements: Array.isArray(wcc.achievements) ? wcc.achievements : prev.achievements,
        isActive: whyChooseUsSection?.isActive ?? true,
      }));

      const gallerySection = (res.data?.sections || []).find(s => s.sectionType === 'gallery' && !s.isGlobal);
      const gc = gallerySection?.content || gallerySection || {};
      setGallery(prev => ({
        ...prev,
        title: gc.title || 'Gallery',
        images: Array.isArray(gc.images) ? gc.images : prev.images,
        isActive: gallerySection?.isActive ?? true,
      }));

      const infrastructureSection = (res.data?.sections || []).find(s => s.sectionType === 'infrastructure' && !s.isGlobal);
      const ic = infrastructureSection?.content || infrastructureSection || {};
      setInfrastructure(prev => ({
        ...prev,
        title: ic.title || 'Our Infrastructure',
        features: Array.isArray(ic.features) ? ic.features : prev.features,
        highlights: Array.isArray(ic.highlights) ? ic.highlights : prev.highlights,
        highlightsTitle: ic.highlightsTitle || prev.highlightsTitle,
        isActive: infrastructureSection?.isActive ?? true,
      }));

      const ctaSection = (res.data?.sections || []).find(s => s.sectionType === 'cta' && !s.isGlobal);
      const cc = ctaSection?.content || ctaSection || {};
      setCta({
        heading: cc.heading || 'Ready to Work With Us?',
        description: cc.description || '',
        buttonText: cc.buttonText || 'Get In Touch',
        buttonLink: cc.buttonLink || '/contact',
        isActive: ctaSection?.isActive ?? true,
      });
    })();
  }, []);

  const onChange = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setHero(prev => ({ ...prev, [key]: value }));
  };

  const onStoryChange = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setStory(prev => ({ ...prev, [key]: value }));
  };

  const onMvvChange = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setMvv(prev => ({ ...prev, [key]: value }));
  };

  const onWhatWeDoChange = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setWhatWeDo(prev => ({ ...prev, [key]: value }));
  };

  const onExpertiseChange = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setExpertise(prev => ({ ...prev, [key]: value }));
  };

  const onTeamChange = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setTeam(prev => ({ ...prev, [key]: value }));
  };

  const onWhyChooseUsChange = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setWhyChooseUs(prev => ({ ...prev, [key]: value }));
  };

  const onGalleryChange = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setGallery(prev => ({ ...prev, [key]: value }));
  };

  const onInfrastructureChange = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setInfrastructure(prev => ({ ...prev, [key]: value }));
  };

  const onCtaChange = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setCta(prev => ({ ...prev, [key]: value }));
  };

  const asArray = (v) => (Array.isArray(v) ? v : []);

  const onSave = async () => {
    const safePage = page || { metaTitle: '', metaDescription: '', sections: [] };

    setLoading(true);
    try {
      // Build new sections array: upsert hero section, keep others unchanged.
      const existingSections = safePage.sections || [];
      const existingHeroIndex = existingSections.findIndex(s => !s.isGlobal && s.sectionType === 'hero');

      const nextHeroSection = {
        sectionId: heroSectionId,
        sectionType: 'hero',
        isGlobal: false,
        order: existingHeroIndex >= 0 ? existingSections[existingHeroIndex].order : 0,
        isActive: !!hero.isActive,
        content: {
          badge: hero.badge,
          headingLine1: hero.headingLine1,
          headingLine2: hero.headingLine2,
          description: hero.description,
          features: hero.features,
        },
        visualSettings: {},
      };

      const upsert = (arr, predicate, section) => {
        const idx = arr.findIndex(predicate);
        if (idx >= 0) return arr.map((s, i) => (i === idx ? section : s));
        return [section, ...arr];
      };

      let nextSections = upsert(existingSections, (s) => !s.isGlobal && s.sectionType === 'hero', nextHeroSection);

      const existingStoryIndex = nextSections.findIndex(s => !s.isGlobal && s.sectionType === 'story');
      const nextStorySection = {
        sectionId: storySectionId,
        sectionType: 'story',
        isGlobal: false,
        order: existingStoryIndex >= 0 ? nextSections[existingStoryIndex].order : 1,
        isActive: !!story.isActive,
        content: {
          image: story.image,
          imageAlt: story.imageAlt,
          title: story.title,
          paragraph1: story.paragraph1,
          paragraph2: story.paragraph2,
          ctaText: story.ctaText,
          ctaLink: story.ctaLink,
        },
        visualSettings: {},
      };

      nextSections = upsert(nextSections, (s) => !s.isGlobal && s.sectionType === 'story', nextStorySection);

      const existingMvvIndex = nextSections.findIndex(s => !s.isGlobal && s.sectionType === 'mvv');
      const nextMvvSection = {
        sectionId: mvvSectionId,
        sectionType: 'mvv',
        isGlobal: false,
        order: existingMvvIndex >= 0 ? nextSections[existingMvvIndex].order : 2,
        isActive: !!mvv.isActive,
        content: {
          sectionTitle: mvv.sectionTitle,
          missionTitle: mvv.missionTitle,
          missionText: mvv.missionText,
          visionTitle: mvv.visionTitle,
          visionText: mvv.visionText,
          valuesTitle: mvv.valuesTitle,
          values: mvv.values,
        },
        visualSettings: {},
      };

      nextSections = upsert(nextSections, (s) => !s.isGlobal && s.sectionType === 'mvv', nextMvvSection);

      const existingWhatWeDoIndex = nextSections.findIndex(s => !s.isGlobal && s.sectionType === 'what-we-do');
      const nextWhatWeDoSection = {
        sectionId: whatWeDoSectionId,
        sectionType: 'what-we-do',
        isGlobal: false,
        order: existingWhatWeDoIndex >= 0 ? nextSections[existingWhatWeDoIndex].order : 3,
        isActive: !!whatWeDo.isActive,
        content: {
          title: whatWeDo.title,
          services: whatWeDo.services,
        },
        visualSettings: {},
      };

      nextSections = upsert(nextSections, (s) => !s.isGlobal && s.sectionType === 'what-we-do', nextWhatWeDoSection);

      const existingExpertiseIndex = nextSections.findIndex(s => !s.isGlobal && s.sectionType === 'expertise');
      const nextExpertiseSection = {
        sectionId: expertiseSectionId,
        sectionType: 'expertise',
        isGlobal: false,
        order: existingExpertiseIndex >= 0 ? nextSections[existingExpertiseIndex].order : 4,
        isActive: !!expertise.isActive,
        content: {
          title: expertise.title,
          items: expertise.items,
        },
        visualSettings: {},
      };

      nextSections = upsert(nextSections, (s) => !s.isGlobal && s.sectionType === 'expertise', nextExpertiseSection);

      const existingTeamIndex = nextSections.findIndex(s => !s.isGlobal && s.sectionType === 'team');
      const nextTeamSection = {
        sectionId: teamSectionId,
        sectionType: 'team',
        isGlobal: false,
        order: existingTeamIndex >= 0 ? nextSections[existingTeamIndex].order : 5,
        isActive: !!team.isActive,
        content: {
          title: team.title,
          members: team.members,
        },
        visualSettings: {},
      };

      nextSections = upsert(nextSections, (s) => !s.isGlobal && s.sectionType === 'team', nextTeamSection);

      const existingWhyChooseUsIndex = nextSections.findIndex(s => !s.isGlobal && s.sectionType === 'why-choose-us');
      const nextWhyChooseUsSection = {
        sectionId: whyChooseUsSectionId,
        sectionType: 'why-choose-us',
        isGlobal: false,
        order: existingWhyChooseUsIndex >= 0 ? nextSections[existingWhyChooseUsIndex].order : 6,
        isActive: !!whyChooseUs.isActive,
        content: {
          title: whyChooseUs.title,
          reasons: whyChooseUs.reasons,
          achievements: whyChooseUs.achievements,
        },
        visualSettings: {},
      };

      nextSections = upsert(nextSections, (s) => !s.isGlobal && s.sectionType === 'why-choose-us', nextWhyChooseUsSection);

      const existingGalleryIndex = nextSections.findIndex(s => !s.isGlobal && s.sectionType === 'gallery');
      const nextGallerySection = {
        sectionId: gallerySectionId,
        sectionType: 'gallery',
        isGlobal: false,
        order: existingGalleryIndex >= 0 ? nextSections[existingGalleryIndex].order : 7,
        isActive: !!gallery.isActive,
        content: {
          title: gallery.title,
          images: gallery.images,
        },
        visualSettings: {},
      };

      nextSections = upsert(nextSections, (s) => !s.isGlobal && s.sectionType === 'gallery', nextGallerySection);

      const existingInfrastructureIndex = nextSections.findIndex(s => !s.isGlobal && s.sectionType === 'infrastructure');
      const nextInfrastructureSection = {
        sectionId: infrastructureSectionId,
        sectionType: 'infrastructure',
        isGlobal: false,
        order: existingInfrastructureIndex >= 0 ? nextSections[existingInfrastructureIndex].order : 8,
        isActive: !!infrastructure.isActive,
        content: {
          title: infrastructure.title,
          features: infrastructure.features,
          highlights: infrastructure.highlights,
          highlightsTitle: infrastructure.highlightsTitle,
        },
        visualSettings: {},
      };

      nextSections = upsert(nextSections, (s) => !s.isGlobal && s.sectionType === 'infrastructure', nextInfrastructureSection);

      const existingCtaIndex = nextSections.findIndex(s => !s.isGlobal && s.sectionType === 'cta');
      const nextCtaSection = {
        sectionId: ctaSectionId,
        sectionType: 'cta',
        isGlobal: false,
        order: existingCtaIndex >= 0 ? nextSections[existingCtaIndex].order : 9,
        isActive: !!cta.isActive,
        content: {
          heading: cta.heading,
          description: cta.description,
          buttonText: cta.buttonText,
          buttonLink: cta.buttonLink,
        },
        visualSettings: {},
      };

      nextSections = upsert(nextSections, (s) => !s.isGlobal && s.sectionType === 'cta', nextCtaSection);

      // Normalize sections to match backend validation exactly.
      const normalizedSections = nextSections
        .slice()
        .sort((a, b) => (a.order ?? 999) - (b.order ?? 999))
        .map((s, idx) => {
          const isGlobal = !!s.isGlobal;
          const base = {
            sectionId: String(s.sectionId),
            order: idx,
            isActive: s.isActive !== false,
            isGlobal,
          };

          if (isGlobal) {
            return {
              ...base,
              globalSectionRef: String(s.globalSectionRef || '').toLowerCase(),
            };
          }

          return {
            ...base,
            sectionType: String(s.sectionType || '').toLowerCase(),
            content: s.content || {},
            visualSettings: s.visualSettings || {},
          };
        })
        // Drop invalid global refs to avoid 400s.
        .filter((s) => (s.isGlobal ? !!s.globalSectionRef : !!s.sectionType));

      const res = await apiUtils.cms.updatePage('about', {
        metaTitle: safePage.metaTitle || '',
        metaDescription: safePage.metaDescription || '',
        sections: normalizedSections,
      });

      if (!res.success) {
        toast.error(res.error || 'Failed to save');
        return;
      }

      setPage(res.data);
      toast.success('About page saved');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">About</h1>
        </div>
        <button
          onClick={onSave}
          disabled={loading}
          className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 disabled:opacity-60"
        >
          {loading ? 'Saving…' : 'Save'}
        </button>
      </div>

      <div className="space-y-4">
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setHeroOpen(v => !v)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
            aria-expanded={heroOpen}
          >
            <div className="text-left">
              <div className="text-sm font-semibold text-gray-900">Hero</div>
              <div className="text-xs text-gray-500">sectionId: <code>{heroSectionId}</code></div>
            </div>
            <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${heroOpen ? 'rotate-180' : ''}`} />
          </button>

          {heroOpen && (
            <div className="p-4 border-t border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className="text-xs text-gray-500">Edit fields below</div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={!!hero.isActive} onChange={onChange('isActive')} />
                  Active
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Badge Text</label>
                  <input value={hero.badge} onChange={onChange('badge')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                  <p className="text-xs text-gray-500 mt-1">e.g., "✨ Who We Are"</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Heading Line 1</label>
                  <input value={hero.headingLine1} onChange={onChange('headingLine1')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Heading Line 2 (Gradient)</label>
                  <input value={hero.headingLine2} onChange={onChange('headingLine2')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea value={hero.description} onChange={onChange('description')} rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Features (separated by •)</label>
                  <input value={hero.features} onChange={onChange('features')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                  <p className="text-xs text-gray-500 mt-1">e.g., "Quality Driven • Globally Trusted • Customer Focused"</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Our Story Section */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setStoryOpen(v => !v)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
            aria-expanded={storyOpen}
          >
            <div className="text-left">
              <div className="text-sm font-semibold text-gray-900">Our Story</div>
              <div className="text-xs text-gray-500">sectionId: <code>{storySectionId}</code></div>
            </div>
            <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${storyOpen ? 'rotate-180' : ''}`} />
          </button>

          {storyOpen && (
            <div className="p-4 border-t border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className="text-xs text-gray-500">Edit story content</div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={!!story.isActive} onChange={onStoryChange('isActive')} />
                  Active
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
                  <ImageDropzone
                    value={story.image}
                    onChange={(url) => setStory(prev => ({ ...prev, image: url }))}
                    label="Story image"
                    previewHeight="h-48"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Image Alt Text</label>
                  <input value={story.imageAlt} onChange={onStoryChange('imageAlt')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input value={story.title} onChange={onStoryChange('title')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Paragraph 1</label>
                  <textarea value={story.paragraph1} onChange={onStoryChange('paragraph1')} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Paragraph 2</label>
                  <textarea value={story.paragraph2} onChange={onStoryChange('paragraph2')} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CTA Button Text</label>
                  <input value={story.ctaText} onChange={onStoryChange('ctaText')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CTA Button Link</label>
                  <input value={story.ctaLink} onChange={onStoryChange('ctaLink')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Mission, Vision & Values Section */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setMvvOpen(v => !v)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
            aria-expanded={mvvOpen}
          >
            <div className="text-left">
              <div className="text-sm font-semibold text-gray-900">Mission, Vision & Values</div>
              <div className="text-xs text-gray-500">sectionId: <code>{mvvSectionId}</code></div>
            </div>
            <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${mvvOpen ? 'rotate-180' : ''}`} />
          </button>

          {mvvOpen && (
            <div className="p-4 border-t border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className="text-xs text-gray-500">Edit mission, vision & values</div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={!!mvv.isActive} onChange={onMvvChange('isActive')} />
                  Active
                </label>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Section Title</label>
                  <input value={mvv.sectionTitle} onChange={onMvvChange('sectionTitle')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>

                <div className="border border-gray-200 rounded-xl p-4">
                  <div className="text-sm font-semibold text-gray-900 mb-3">Mission</div>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                      <input value={mvv.missionTitle} onChange={onMvvChange('missionTitle')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Text</label>
                      <textarea value={mvv.missionText} onChange={onMvvChange('missionText')} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                    </div>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-xl p-4">
                  <div className="text-sm font-semibold text-gray-900 mb-3">Vision</div>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                      <input value={mvv.visionTitle} onChange={onMvvChange('visionTitle')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Text</label>
                      <textarea value={mvv.visionText} onChange={onMvvChange('visionText')} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                    </div>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-xl p-4">
                  <div className="text-sm font-semibold text-gray-900 mb-3">Core Values</div>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                      <input value={mvv.valuesTitle} onChange={onMvvChange('valuesTitle')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">Values List</label>
                        <button
                          type="button"
                          onClick={() => setMvv(prev => ({ ...prev, values: [...prev.values, ''] }))}
                          className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                          Add Value
                        </button>
                      </div>
                      <div className="space-y-2">
                        {(mvv.values || []).map((value, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <input
                              value={value}
                              onChange={(e) => {
                                const newValues = [...mvv.values];
                                newValues[idx] = e.target.value;
                                setMvv(prev => ({ ...prev, values: newValues }));
                              }}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                              placeholder="Value name"
                            />
                            <button
                              type="button"
                              onClick={() => setMvv(prev => ({ ...prev, values: prev.values.filter((_, i) => i !== idx) }))}
                              className="px-3 py-2 text-sm text-red-700 border border-red-200 rounded-lg hover:bg-red-50"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* What We Do Section */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setWhatWeDoOpen(v => !v)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
            aria-expanded={whatWeDoOpen}
          >
            <div className="text-left">
              <div className="text-sm font-semibold text-gray-900">What We Do</div>
              <div className="text-xs text-gray-500">sectionId: <code>{whatWeDoSectionId}</code></div>
            </div>
            <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${whatWeDoOpen ? 'rotate-180' : ''}`} />
          </button>

          {whatWeDoOpen && (
            <div className="p-4 border-t border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className="text-xs text-gray-500">Edit services</div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={!!whatWeDo.isActive} onChange={onWhatWeDoChange('isActive')} />
                  Active
                </label>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Section Title</label>
                  <input value={whatWeDo.title} onChange={onWhatWeDoChange('title')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-gray-900">Services</div>
                  <button
                    type="button"
                    onClick={() => setWhatWeDo(prev => ({
                      ...prev,
                      services: [...prev.services, { title: '', description: '' }],
                    }))}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Add Service
                  </button>
                </div>

                <div className="space-y-3">
                  {(whatWeDo.services || []).map((service, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-sm font-medium text-gray-900">Service #{idx + 1}</div>
                        <button
                          type="button"
                          onClick={() => setWhatWeDo(prev => ({
                            ...prev,
                            services: prev.services.filter((_, i) => i !== idx),
                          }))}
                          className="px-3 py-1 text-sm text-red-700 border border-red-200 rounded-lg hover:bg-red-50"
                        >
                          Remove
                        </button>
                      </div>

                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                          <input
                            value={service.title || ''}
                            onChange={(e) => {
                              const newServices = [...whatWeDo.services];
                              newServices[idx] = { ...newServices[idx], title: e.target.value };
                              setWhatWeDo(prev => ({ ...prev, services: newServices }));
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                          <textarea
                            value={service.description || ''}
                            onChange={(e) => {
                              const newServices = [...whatWeDo.services];
                              newServices[idx] = { ...newServices[idx], description: e.target.value };
                              setWhatWeDo(prev => ({ ...prev, services: newServices }));
                            }}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  {(whatWeDo.services || []).length === 0 && (
                    <div className="text-sm text-gray-500">No services yet. Click "Add Service".</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Our Expertise Section */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setExpertiseOpen(v => !v)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
            aria-expanded={expertiseOpen}
          >
            <div className="text-left">
              <div className="text-sm font-semibold text-gray-900">Our Expertise</div>
              <div className="text-xs text-gray-500">sectionId: <code>{expertiseSectionId}</code></div>
            </div>
            <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${expertiseOpen ? 'rotate-180' : ''}`} />
          </button>

          {expertiseOpen && (
            <div className="p-4 border-t border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className="text-xs text-gray-500">Edit expertise items</div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={!!expertise.isActive} onChange={onExpertiseChange('isActive')} />
                  Active
                </label>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Section Title</label>
                  <input value={expertise.title} onChange={onExpertiseChange('title')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">Expertise Items</label>
                    <button
                      type="button"
                      onClick={() => setExpertise(prev => ({ ...prev, items: [...prev.items, ''] }))}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Add Item
                    </button>
                  </div>
                  <div className="space-y-2">
                    {(expertise.items || []).map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <input
                          value={item}
                          onChange={(e) => {
                            const newItems = [...expertise.items];
                            newItems[idx] = e.target.value;
                            setExpertise(prev => ({ ...prev, items: newItems }));
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                          placeholder="Expertise name"
                        />
                        <button
                          type="button"
                          onClick={() => setExpertise(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }))}
                          className="px-3 py-2 text-sm text-red-700 border border-red-200 rounded-lg hover:bg-red-50"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                  {(expertise.items || []).length === 0 && (
                    <div className="text-sm text-gray-500 mt-2">No items yet. Click "Add Item".</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Our Leadership Team Section */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setTeamOpen(v => !v)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
            aria-expanded={teamOpen}
          >
            <div className="text-left">
              <div className="text-sm font-semibold text-gray-900">Our Leadership Team</div>
              <div className="text-xs text-gray-500">sectionId: <code>{teamSectionId}</code></div>
            </div>
            <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${teamOpen ? 'rotate-180' : ''}`} />
          </button>

          {teamOpen && (
            <div className="p-4 border-t border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className="text-xs text-gray-500">Edit team members</div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={!!team.isActive} onChange={onTeamChange('isActive')} />
                  Active
                </label>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Section Title</label>
                  <input value={team.title} onChange={onTeamChange('title')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-gray-900">Team Members</div>
                  <button
                    type="button"
                    onClick={() => setTeam(prev => ({
                      ...prev,
                      members: [...prev.members, { name: '', position: '', bio: '', image: '' }],
                    }))}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Add Member
                  </button>
                </div>

                <div className="space-y-3">
                  {(team.members || []).map((member, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-sm font-medium text-gray-900">Member #{idx + 1}</div>
                        <button
                          type="button"
                          onClick={() => setTeam(prev => ({
                            ...prev,
                            members: prev.members.filter((_, i) => i !== idx),
                          }))}
                          className="px-3 py-1 text-sm text-red-700 border border-red-200 rounded-lg hover:bg-red-50"
                        >
                          Remove
                        </button>
                      </div>

                      <div className="space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                            <input
                              value={member.name || ''}
                              onChange={(e) => {
                                const newMembers = [...team.members];
                                newMembers[idx] = { ...newMembers[idx], name: e.target.value };
                                setTeam(prev => ({ ...prev, members: newMembers }));
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Position</label>
                            <input
                              value={member.position || ''}
                              onChange={(e) => {
                                const newMembers = [...team.members];
                                newMembers[idx] = { ...newMembers[idx], position: e.target.value };
                                setTeam(prev => ({ ...prev, members: newMembers }));
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                          <textarea
                            value={member.bio || ''}
                            onChange={(e) => {
                              const newMembers = [...team.members];
                              newMembers[idx] = { ...newMembers[idx], bio: e.target.value };
                              setTeam(prev => ({ ...prev, members: newMembers }));
                            }}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
                          <ImageDropzone
                            value={member.image || ''}
                            onChange={(url) => {
                              const newMembers = [...team.members];
                              newMembers[idx] = { ...newMembers[idx], image: url };
                              setTeam(prev => ({ ...prev, members: newMembers }));
                            }}
                            label="Team member photo"
                            previewHeight="h-48"
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  {(team.members || []).length === 0 && (
                    <div className="text-sm text-gray-500">No team members yet. Click "Add Member".</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Why Choose Us Section */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setWhyChooseUsOpen(v => !v)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
            aria-expanded={whyChooseUsOpen}
          >
            <div className="text-left">
              <div className="text-sm font-semibold text-gray-900">Why Choose Us</div>
              <div className="text-xs text-gray-500">sectionId: <code>{whyChooseUsSectionId}</code></div>
            </div>
            <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${whyChooseUsOpen ? 'rotate-180' : ''}`} />
          </button>

          {whyChooseUsOpen && (
            <div className="p-4 border-t border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className="text-xs text-gray-500">Edit reasons and achievements</div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={!!whyChooseUs.isActive} onChange={onWhyChooseUsChange('isActive')} />
                  Active
                </label>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Section Title</label>
                  <input value={whyChooseUs.title} onChange={onWhyChooseUsChange('title')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>

                {/* Reasons */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-semibold text-gray-900">Reasons</label>
                    <button
                      type="button"
                      onClick={() => setWhyChooseUs(prev => ({
                        ...prev,
                        reasons: [...prev.reasons, { icon: 'award', title: '', description: '' }],
                      }))}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Add Reason
                    </button>
                  </div>
                  <div className="space-y-3">
                    {(whyChooseUs.reasons || []).map((reason, idx) => (
                      <div key={idx} className="border border-gray-200 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="text-sm font-medium text-gray-900">Reason #{idx + 1}</div>
                          <button
                            type="button"
                            onClick={() => setWhyChooseUs(prev => ({
                              ...prev,
                              reasons: prev.reasons.filter((_, i) => i !== idx),
                            }))}
                            className="px-3 py-1 text-sm text-red-700 border border-red-200 rounded-lg hover:bg-red-50"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Icon</label>
                            <select
                              value={reason.icon || 'award'}
                              onChange={(e) => {
                                const newReasons = [...whyChooseUs.reasons];
                                newReasons[idx] = { ...newReasons[idx], icon: e.target.value };
                                setWhyChooseUs(prev => ({ ...prev, reasons: newReasons }));
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            >
                              <option value="award">Award</option>
                              <option value="target">Target</option>
                              <option value="globe">Globe</option>
                              <option value="users">Users</option>
                              <option value="lightbulb">Lightbulb</option>
                              <option value="clock">Clock</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                            <input
                              value={reason.title || ''}
                              onChange={(e) => {
                                const newReasons = [...whyChooseUs.reasons];
                                newReasons[idx] = { ...newReasons[idx], title: e.target.value };
                                setWhyChooseUs(prev => ({ ...prev, reasons: newReasons }));
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                            <textarea
                              value={reason.description || ''}
                              onChange={(e) => {
                                const newReasons = [...whyChooseUs.reasons];
                                newReasons[idx] = { ...newReasons[idx], description: e.target.value };
                                setWhyChooseUs(prev => ({ ...prev, reasons: newReasons }));
                              }}
                              rows={2}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Achievements */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-semibold text-gray-900">Achievements / Certifications</label>
                    <button
                      type="button"
                      onClick={() => setWhyChooseUs(prev => ({
                        ...prev,
                        achievements: [...prev.achievements, { title: '', description: '' }],
                      }))}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Add Achievement
                    </button>
                  </div>
                  <div className="space-y-3">
                    {(whyChooseUs.achievements || []).map((achievement, idx) => (
                      <div key={idx} className="border border-gray-200 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="text-sm font-medium text-gray-900">Achievement #{idx + 1}</div>
                          <button
                            type="button"
                            onClick={() => setWhyChooseUs(prev => ({
                              ...prev,
                              achievements: prev.achievements.filter((_, i) => i !== idx),
                            }))}
                            className="px-3 py-1 text-sm text-red-700 border border-red-200 rounded-lg hover:bg-red-50"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                            <input
                              value={achievement.title || ''}
                              onChange={(e) => {
                                const newAchievements = [...whyChooseUs.achievements];
                                newAchievements[idx] = { ...newAchievements[idx], title: e.target.value };
                                setWhyChooseUs(prev => ({ ...prev, achievements: newAchievements }));
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                            <input
                              value={achievement.description || ''}
                              onChange={(e) => {
                                const newAchievements = [...whyChooseUs.achievements];
                                newAchievements[idx] = { ...newAchievements[idx], description: e.target.value };
                                setWhyChooseUs(prev => ({ ...prev, achievements: newAchievements }));
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Gallery Section */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setGalleryOpen(v => !v)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
            aria-expanded={galleryOpen}
          >
            <div className="text-left">
              <div className="text-sm font-semibold text-gray-900">Gallery</div>
              <div className="text-xs text-gray-500">sectionId: <code>{gallerySectionId}</code></div>
            </div>
            <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${galleryOpen ? 'rotate-180' : ''}`} />
          </button>

          {galleryOpen && (
            <div className="p-4 border-t border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className="text-xs text-gray-500">Edit gallery images</div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={!!gallery.isActive} onChange={onGalleryChange('isActive')} />
                  Active
                </label>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Section Title</label>
                  <input value={gallery.title} onChange={onGalleryChange('title')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-gray-900">Gallery Images</div>
                  <button
                    type="button"
                    onClick={() => setGallery(prev => ({
                      ...prev,
                      images: [...prev.images, { title: '', description: '', image: '', alt: '' }],
                    }))}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Add Image
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {(gallery.images || []).map((img, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-xl p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs font-medium text-gray-900">Image #{idx + 1}</div>
                        <button
                          type="button"
                          onClick={() => setGallery(prev => ({
                            ...prev,
                            images: prev.images.filter((_, i) => i !== idx),
                          }))}
                          className="px-2 py-1 text-xs text-red-700 border border-red-200 rounded hover:bg-red-50"
                        >
                          Remove
                        </button>
                      </div>
                      <div className="space-y-2">
                        <input
                          value={img.title || ''}
                          onChange={(e) => {
                            const newImages = [...gallery.images];
                            newImages[idx] = { ...newImages[idx], title: e.target.value };
                            setGallery(prev => ({ ...prev, images: newImages }));
                          }}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          placeholder="Title"
                        />
                        <input
                          value={img.description || ''}
                          onChange={(e) => {
                            const newImages = [...gallery.images];
                            newImages[idx] = { ...newImages[idx], description: e.target.value };
                            setGallery(prev => ({ ...prev, images: newImages }));
                          }}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          placeholder="Description"
                        />
                        <ImageDropzone
                          value={img.image || ''}
                          onChange={(url) => {
                            const newImages = [...gallery.images];
                            newImages[idx] = { ...newImages[idx], image: url };
                            setGallery(prev => ({ ...prev, images: newImages }));
                          }}
                          label="Gallery image"
                          previewHeight="h-32"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Our Infrastructure Section */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setInfrastructureOpen(v => !v)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
            aria-expanded={infrastructureOpen}
          >
            <div className="text-left">
              <div className="text-sm font-semibold text-gray-900">Our Infrastructure</div>
              <div className="text-xs text-gray-500">sectionId: <code>{infrastructureSectionId}</code></div>
            </div>
            <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${infrastructureOpen ? 'rotate-180' : ''}`} />
          </button>

          {infrastructureOpen && (
            <div className="p-4 border-t border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className="text-xs text-gray-500">Edit infrastructure details</div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={!!infrastructure.isActive} onChange={onInfrastructureChange('isActive')} />
                  Active
                </label>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Section Title</label>
                  <input value={infrastructure.title} onChange={onInfrastructureChange('title')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>

                {/* Features */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-semibold text-gray-900">Features</label>
                    <button
                      type="button"
                      onClick={() => setInfrastructure(prev => ({
                        ...prev,
                        features: [...prev.features, { title: '', description: '' }],
                      }))}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Add Feature
                    </button>
                  </div>
                  <div className="space-y-3">
                    {(infrastructure.features || []).map((feature, idx) => (
                      <div key={idx} className="border border-gray-200 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="text-sm font-medium text-gray-900">Feature #{idx + 1}</div>
                          <button
                            type="button"
                            onClick={() => setInfrastructure(prev => ({
                              ...prev,
                              features: prev.features.filter((_, i) => i !== idx),
                            }))}
                            className="px-3 py-1 text-sm text-red-700 border border-red-200 rounded-lg hover:bg-red-50"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                            <input
                              value={feature.title || ''}
                              onChange={(e) => {
                                const newFeatures = [...infrastructure.features];
                                newFeatures[idx] = { ...newFeatures[idx], title: e.target.value };
                                setInfrastructure(prev => ({ ...prev, features: newFeatures }));
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                            <textarea
                              value={feature.description || ''}
                              onChange={(e) => {
                                const newFeatures = [...infrastructure.features];
                                newFeatures[idx] = { ...newFeatures[idx], description: e.target.value };
                                setInfrastructure(prev => ({ ...prev, features: newFeatures }));
                              }}
                              rows={2}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Highlights */}
                <div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Highlights Section Title</label>
                    <input value={infrastructure.highlightsTitle} onChange={onInfrastructureChange('highlightsTitle')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                  </div>
                  <div className="mt-3">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">Highlight Items</label>
                      <button
                        type="button"
                        onClick={() => setInfrastructure(prev => ({ ...prev, highlights: [...prev.highlights, ''] }))}
                        className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        Add Highlight
                      </button>
                    </div>
                    <div className="space-y-2">
                      {(infrastructure.highlights || []).map((highlight, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <input
                            value={highlight}
                            onChange={(e) => {
                              const newHighlights = [...infrastructure.highlights];
                              newHighlights[idx] = e.target.value;
                              setInfrastructure(prev => ({ ...prev, highlights: newHighlights }));
                            }}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                            placeholder="Highlight item"
                          />
                          <button
                            type="button"
                            onClick={() => setInfrastructure(prev => ({ ...prev, highlights: prev.highlights.filter((_, i) => i !== idx) }))}
                            className="px-3 py-2 text-sm text-red-700 border border-red-200 rounded-lg hover:bg-red-50"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* CTA Section */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setCtaOpen(v => !v)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
            aria-expanded={ctaOpen}
          >
            <div className="text-left">
              <div className="text-sm font-semibold text-gray-900">Call to Action (CTA)</div>
              <div className="text-xs text-gray-500">sectionId: <code>{ctaSectionId}</code></div>
            </div>
            <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${ctaOpen ? 'rotate-180' : ''}`} />
          </button>

          {ctaOpen && (
            <div className="p-4 border-t border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className="text-xs text-gray-500">Edit CTA content</div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={!!cta.isActive} onChange={onCtaChange('isActive')} />
                  Active
                </label>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Heading</label>
                  <input value={cta.heading} onChange={onCtaChange('heading')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea value={cta.description} onChange={onCtaChange('description')} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Use \n for line breaks" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
                    <input value={cta.buttonText} onChange={onCtaChange('buttonText')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Button Link</label>
                    <input value={cta.buttonLink} onChange={onCtaChange('buttonLink')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
